<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn
            id="btn-voltar-series"
            :disabled="submittingForm"
            color="primary"
            text
            x-small
            @click="goBackToSeries"
          >
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          {{ pageTitle }}
        </h1>
      </v-col>
    </v-row>

    <ValidationObserver v-slot="{ handleSubmit }">
      <form :disabled="submittingForm" @submit.prevent="handleSubmit(submitForm)">
        <v-row>
          <v-col cols="12">
            <v-card class="mb-4 pa-4" elevation="1">
              <v-card-title class="font-weight-bold mb-4 pa-0 text-subtitle-1">
                Informações Básicas:
              </v-card-title>

              <v-row>
                <v-col cols="12" md="6" lg="4">
                  <ValidationProvider name="Descrição" rules="required" v-slot="{ errors }">
                    <e-label class="required">Descrição</e-label>
                    <v-text-field
                      id="input-descricao"
                      v-model="form.descricao"
                      dense
                      disabled
                      :error-messages="errors"
                      hide-details="auto"
                      outlined
                      placeholder="Digite a descrição da etapa"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6" lg="4">
                  <ValidationProvider name="Curso" rules="required" v-slot="{ errors }">
                    <e-label class="required">Curso</e-label>
                    <e-autocomplete
                      id="autocomplete-curso"
                      v-model="form.curso_id"
                      dense
                      disabled
                      :error-messages="errors"
                      hide-details="auto"
                      :items="sortedCursosList"
                      :item-text="cursoText"
                      :item-value="cursoValue"
                      :loading="loadingCursos"
                      outlined
                      placeholder="Selecione um curso"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6" lg="4">
                  <div class="align-center d-flex mt-6">
                    <v-checkbox
                      id="checkbox-historico"
                      v-model="form.historico"
                      class="mt-0"
                      dense
                      hide-details
                      label="Histórico?"
                    />
                    <v-tooltip max-width="310" top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          id="icon-info-historico"
                          v-bind="attrs"
                          v-on="on"
                          class="ml-1"
                          color="grey"
                          size="20"
                        >
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span class="text-body-1">
                        Marque esta opção para incluir esta etapa nas opções de etapa ao criar o
                        histórico escolar do aluno.
                      </span>
                    </v-tooltip>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="mb-4 pa-4" elevation="1">
              <v-card-title class="font-weight-bold mb-4 pa-0 text-subtitle-1">
                Turnos da Etapa:
              </v-card-title>

              <v-row>
                <v-col cols="12" md="8" lg="6">
                  <e-label>Turnos vinculados</e-label>
                  <e-autocomplete
                    id="autocomplete-turnos"
                    v-model="form.turnoIdList"
                    chips
                    deletable-chips
                    dense
                    hide-details="auto"
                    :items="sortedTurnosList"
                    :item-text="turnoText"
                    :item-value="turnoValue"
                    :loading="loadingTurnos"
                    multiple
                    outlined
                    placeholder="Selecione os turnos desta etapa"
                    :return-object="false"
                    small-chips
                  />
                </v-col>

                <v-col cols="12" md="4" lg="6">
                  <div class="align-start d-flex mt-4">
                    <v-tooltip max-width="425" right>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" class="mt-5" color="grey" size="20">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span class="text-body-1">
                        A configuração Padrão atende aos requisitos normais da escola. Vínculos de
                        turnos adicionais devem ser criados apenas quando a escola possuir
                        configurações curriculares específicas para cada turno. Cada turno com
                        configuração própria terá uma aba exclusiva de componentes curriculares.
                      </span>
                    </v-tooltip>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="pa-4" elevation="1">
              <v-tabs v-model="activeTab" class="mb-4" show-arrows>
                <v-tab v-for="aba in abas" :key="aba.chave" :data-testid="`aba-${aba.chave}`">
                  {{ aba.titulo }}
                  <v-chip class="ml-2" x-small>{{ contarSelecionadas(aba.chave) }}</v-chip>
                </v-tab>
              </v-tabs>

              <v-card-title
                class="align-center d-flex font-weight-bold justify-space-between mb-4 pa-0 text-subtitle-1"
              >
                <div class="align-center d-flex">
                  Componentes Curriculares:
                  <v-chip class="ml-2" color="primary">
                    {{ selectedDisciplinesCount }} selecionadas
                  </v-chip>
                </div>
                <div class="align-center d-flex">
                  <v-btn
                    v-if="!isAbaPadrao"
                    id="btn-copiar-do-padrao"
                    class="mr-4"
                    color="primary"
                    outlined
                    small
                    @click="copiarDoPadrao"
                  >
                    <v-icon left small>mdi-content-copy</v-icon>
                    Copiar do padrão
                  </v-btn>
                  <v-checkbox
                    id="checkbox-marcar-todos"
                    v-model="isSelectAll"
                    class="mr-5"
                    :label="selectAllLabel"
                    @change="toggleAllDisciplines"
                  />
                </div>
              </v-card-title>

              <e-label class="mb-3">
                {{ abaAtualDescricao }}
              </e-label>

              <v-row>
                <v-col
                  v-for="disciplina in sortedDisciplinasList"
                  :key="disciplina.id"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                >
                  <v-card
                    class="mb-3 pa-3"
                    :class="{ 'selected-disciplina': isDisciplineSelected(disciplina.id) }"
                    elevation="1"
                    outlined
                  >
                    <div class="align-start d-flex">
                      <v-checkbox
                        :id="`checkbox-disciplina-${disciplina.id}`"
                        v-model="configuracaoAtual.disciplinaIdList"
                        class="flex-grow-1 mt-0"
                        :data-testid="`disciplina-checkbox-${disciplina.id}`"
                        :disabled="shouldDisableDisciplina(disciplina.id)"
                        hide-details
                        :label="disciplina.descricao"
                        :value="disciplina.id"
                      />

                      <v-tooltip
                        v-if="shouldDisableDisciplina(disciplina.id)"
                        bottom
                        color="#000000"
                        max-width="450"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" class="ml-4" color="primary" size="20">
                            mdi-information-outline
                          </v-icon>
                        </template>

                        <div class="tooltip-registros">
                          <template v-if="getRegistrosVinculados(disciplina.id).length">
                            <div class="mb-2">
                              Não é possível desvincular essa disciplina pois existem registros
                              vinculados a ela nesta etapa:
                            </div>

                            <div class="registros-table">
                              <div class="font-weight-bold registros-row">
                                <span>Tipo</span>
                                <span>Quantidade</span>
                              </div>

                              <div
                                v-for="registro in getRegistrosVinculados(disciplina.id)"
                                :key="registro.label"
                                class="registros-row"
                              >
                                <span>{{ registro.label }}</span>
                                <span>{{ registro.quantidade }}</span>
                              </div>
                            </div>
                          </template>

                          <template v-else>
                            <div class="mb-2">
                              Não é possível desvincular essa disciplina pois ela já está vinculada
                              à gestões nesta etapa.
                            </div>
                          </template>

                          <div class="mt-3">
                            Entre em contato com a equipe pedagógica para esclarecimentos.
                          </div>
                        </div>
                      </v-tooltip>
                    </div>

                    <v-expand-transition>
                      <div v-if="isDisciplineSelected(disciplina.id)" class="mt-3">
                        <ValidationProvider
                          :name="`Carga horária - ${disciplina.descricao}`"
                          rules="required|min_value:1|max_value:999"
                          v-slot="{ errors }"
                        >
                          <template
                            v-if="
                              !configuracaoDaDisciplina(disciplina.id).disciplina_extra_curricular
                            "
                          >
                            <e-label class="required">Carga horária (horas)</e-label>
                            <v-text-field
                              :id="`campo-carga-horaria-${disciplina.id}`"
                              v-model.number="
                                configuracaoDaDisciplina(disciplina.id).carga_horaria_disciplina
                              "
                              class="carga-horaria-field"
                              dense
                              :error-messages="errors"
                              hide-details="auto"
                              max="999"
                              min="1"
                              outlined
                              placeholder="Ex: 180"
                              type="number"
                              v-mask="'###'"
                            />
                          </template>
                        </ValidationProvider>

                        <div class="align-center d-flex mt-2">
                          <v-switch
                            :id="`input-extra-curricular-${disciplina.id}`"
                            v-model="
                              configuracaoDaDisciplina(disciplina.id).disciplina_extra_curricular
                            "
                            hide-details
                            label="Complementar"
                            @change="clearWorkload(disciplina.id)"
                          />
                          <v-tooltip max-width="425" right>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                                class="ml-4 mt-5"
                                color="grey"
                                size="20"
                              >
                                mdi-information-outline
                              </v-icon>
                            </template>
                            <span class="text-body-1">
                              Ao ativar a opção 'Complementar' a carga horária do componente
                              curricular complementar será zero e o componente curricular não
                              aparecerá nos relatórios e dashboards.
                            </span>
                          </v-tooltip>
                        </div>

                        <div class="align-center d-flex mt-2">
                          <v-switch
                            :id="`input-permite-plano-${disciplina.id}`"
                            v-model="configuracaoDaDisciplina(disciplina.id).permite_plano"
                            hide-details
                            label="Permite lançamento de plano"
                          />
                          <v-tooltip max-width="425" right>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                                class="ml-4 mt-5"
                                color="grey"
                                size="20"
                              >
                                mdi-information-outline
                              </v-icon>
                            </template>
                            <span class="text-body-1">
                              Quando ativado, esta disciplina permitirá lançamento de plano
                              pedagógico.
                            </span>
                          </v-tooltip>
                        </div>
                      </div>
                    </v-expand-transition>
                  </v-card>
                </v-col>
              </v-row>

              <div v-if="isEmptyDisciplinesList" class="py-8 text-center">
                <v-icon class="mb-2" color="grey lighten-1" size="48">mdi-book-off</v-icon>
                <div class="grey--text text-body-1">Nenhuma disciplina disponível</div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" class="mt-6">
            <v-card class="pa-4" color="grey lighten-4" elevation="0">
              <v-row>
                <v-col cols="12" md="6" class="d-flex">
                  <v-btn
                    id="btn-limpar-dados"
                    :disabled="submittingForm"
                    block
                    class="mr-md-2"
                    color="primary"
                    outlined
                    @click="clearForm"
                  >
                    <v-icon left>mdi-delete</v-icon>
                    Limpar Dados
                  </v-btn>
                  <v-btn
                    id="btn-enviar-dados"
                    :disabled="submittingForm"
                    :loading="submittingForm"
                    block
                    class="ml-md-2"
                    color="primary"
                    type="submit"
                  >
                    <v-icon left>mdi-check</v-icon>
                    {{ submitButtonLabel }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </form>
    </ValidationObserver>
  </main-template>
</template>

<script>
const TIPOS_DE_REGISTRO = [
  { campo: "quantidade_aulas", label: "Aulas lançadas" },
  { campo: "quantidade_presencas", label: "Frequências lançadas" },
  { campo: "quantidade_notas", label: "Notas lançadas" },
  { campo: "quantidade_planos", label: "Planos cadastrados" },
];

const CHAVE_PADRAO = "padrao";

export default {
  name: "NewEditSeriePage",

  props: {
    editing: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    return {
      activeTab: 0,
      // { padrao: { disciplinaIdList: [], disciplinas: { [id]: {...} } }, [turnoId]: {...} }
      configuracoes: {},
      cursosList: [],
      disciplinasComGestao: {},
      disciplinasList: [],
      form: {
        curso_id: null,
        descricao: "",
        historico: false,
        id: null,
        turnoIdList: [],
      },
      isSelectAll: false,
      loadingCursos: false,
      loadingTurnos: false,
      submittingForm: false,
      turnosList: [],
    };
  },

  computed: {
    abaAtual() {
      return this.abas[this.activeTab] || this.abas[0];
    },

    abaAtualDescricao() {
      return this.isAbaPadrao
        ? "Configuração padrão: vale para turmas de turnos sem aba própria."
        : `Configuração exclusiva do turno ${this.abaAtual?.titulo}.`;
    },

    abas() {
      return [
        { chave: CHAVE_PADRAO, titulo: "Padrão" },
        ...this.form.turnoIdList.map((turnoId) => ({
          chave: String(turnoId),
          titulo: this.descricaoDoTurno(turnoId),
        })),
      ];
    },

    configuracaoAtual() {
      return this.configuracoes[this.abaAtual?.chave] || { disciplinaIdList: [], disciplinas: {} };
    },

    isAbaPadrao() {
      return this.abaAtual?.chave === CHAVE_PADRAO;
    },

    isEmptyDisciplinesList() {
      return this.disciplinasList.length === 0;
    },

    pageTitle() {
      return this.editing ? "Editando Etapa" : "Criando Etapa";
    },

    selectAllLabel() {
      return this.isSelectAll ? "Desmarcar todos" : "Marcar todos";
    },

    selectedDisciplinesCount() {
      return this.configuracaoAtual.disciplinaIdList.length;
    },

    sortedCursosList() {
      return this.sortByDescricao(this.cursosList);
    },

    sortedDisciplinasList() {
      return this.sortByDescricao(this.disciplinasList);
    },

    sortedTurnosList() {
      return this.sortByDescricao(this.turnosList);
    },

    submitButtonLabel() {
      return this.editing ? "Atualizar Etapa" : "Criar Etapa";
    },
  },

  watch: {
    "configuracaoAtual.disciplinaIdList": {
      deep: true,
      handler(newValue) {
        this.isSelectAll = (newValue || []).length === this.disciplinasList.length;
      },
    },

    "form.turnoIdList": {
      handler(turnoIdList) {
        this.sincronizarConfiguracoes(turnoIdList);
      },
    },
  },

  async mounted() {
    await this.loadData();
  },

  methods: {
    buildDisciplinasPayload(chave) {
      const configuracao = this.configuracoes[chave];
      if (!configuracao) return {};

      return configuracao.disciplinaIdList.reduce((payload, id) => {
        const disciplina = configuracao.disciplinas[id];
        if (!disciplina) return payload;

        return {
          ...payload,
          [id]: {
            carga_horaria_disciplina: disciplina.carga_horaria_disciplina || null,
            disciplina_extra_curricular: disciplina.disciplina_extra_curricular || false,
            permite_plano: disciplina.permite_plano || false,
          },
        };
      }, {});
    },

    clearForm() {
      this.$set(this.configuracoes, this.abaAtual.chave, this.criarConfiguracao());
      this.isSelectAll = false;
    },

    clearWorkload(disciplinaId) {
      const disciplina = this.configuracaoDaDisciplina(disciplinaId);
      if (disciplina.disciplina_extra_curricular) {
        disciplina.carga_horaria_disciplina = null;
      }
    },

    configuracaoDaDisciplina(disciplinaId) {
      return this.configuracaoAtual.disciplinas[disciplinaId] || {};
    },

    contarSelecionadas(chave) {
      return this.configuracoes[chave]?.disciplinaIdList.length || 0;
    },

    copiarDoPadrao() {
      const padrao = this.configuracoes[CHAVE_PADRAO];
      if (!padrao) return;

      this.$set(this.configuracoes, this.abaAtual.chave, this.clonarConfiguracao(padrao));
      this.$toast.success("Configuração Padrão copiada para este turno");
    },

    criarConfiguracao(pivotsPorDisciplinaId = {}) {
      const disciplinaIdList = [];
      const disciplinas = {};

      this.disciplinasList.forEach(({ id }) => {
        const pivot = pivotsPorDisciplinaId[id];

        disciplinas[id] = {
          carga_horaria_disciplina: pivot?.carga_horaria_disciplina ?? null,
          disciplina_extra_curricular: !!pivot?.disciplina_extra_curricular,
          permite_plano: !!pivot?.permite_plano,
        };

        if (pivot) disciplinaIdList.push(id);
      });

      return { disciplinaIdList, disciplinas };
    },

    clonarConfiguracao(configuracao) {
      return {
        disciplinaIdList: [...configuracao.disciplinaIdList],
        disciplinas: Object.entries(configuracao.disciplinas).reduce(
          (clone, [id, valores]) => ({ ...clone, [id]: { ...valores } }),
          {}
        ),
      };
    },

    cursoText(curso) {
      return curso.descricao;
    },

    cursoValue(curso) {
      return curso.id;
    },

    descricaoDoTurno(turnoId) {
      return this.turnosList.find((turno) => turno.id === turnoId)?.descricao || `Turno ${turnoId}`;
    },

    getGestoesDoEscopo(disciplinaId) {
      const gestoes = this.disciplinasComGestao[disciplinaId] || [];

      if (!this.isAbaPadrao) {
        return gestoes.filter((gestao) => String(gestao.turno_id) === this.abaAtual.chave);
      }

      return gestoes.filter(
        (gestao) => !gestao.turno_id || !this.form.turnoIdList.includes(gestao.turno_id)
      );
    },

    getQuantidadeRegistros(disciplinaId, campo) {
      return this.getGestoesDoEscopo(disciplinaId).reduce(
        (total, gestao) => total + (gestao[campo] || 0),
        0
      );
    },

    getRegistrosVinculados(disciplinaId) {
      return TIPOS_DE_REGISTRO.map(({ campo, label }) => ({
        label,
        quantidade: this.getQuantidadeRegistros(disciplinaId, campo),
      })).filter((registro) => registro.quantidade > 0);
    },

    goBackToSeries() {
      this.$router.push({ name: "series" });
    },

    hasGestao(disciplinaId) {
      return this.getGestoesDoEscopo(disciplinaId).length > 0;
    },

    hasRegistrosRelacionados(disciplinaId) {
      return this.getRegistrosVinculados(disciplinaId).length > 0;
    },

    initializeForm(serie) {
      this.form = {
        curso_id: serie?.curso_id || null,
        descricao: serie?.descricao || "",
        historico: serie?.historico || false,
        id: serie?.id || null,
        turnoIdList: (serie?.serie_turnos || []).map((variacao) => variacao.turno_id),
      };

      const configuracoes = {
        [CHAVE_PADRAO]: this.criarConfiguracao(this.mapearPivots(serie?.disciplinas)),
      };

      (serie?.serie_turnos || []).forEach((variacao) => {
        configuracoes[String(variacao.turno_id)] = this.criarConfiguracao(
          this.mapearPivots(variacao.disciplinas)
        );
      });

      this.configuracoes = configuracoes;
      this.activeTab = 0;
    },

    isDisciplineSelected(disciplinaId) {
      return this.configuracaoAtual.disciplinaIdList.includes(disciplinaId);
    },

    async loadCursosData() {
      this.loadingCursos = true;
      try {
        this.cursosList = await this.$services.cursosService.syncAll();
      } finally {
        this.loadingCursos = false;
      }
    },

    async loadData() {
      try {
        this.$loaderService.open("Carregando dados");

        const { serie_id: serieId } = this.$route.params;
        const serie = serieId ? await this.$services.seriesService.getSerie(serieId) : {};

        if (serie?.id) {
          this.disciplinasComGestao = await this.$services.seriesService.getDisciplinasComGestao(
            serie.id
          );
        }

        await Promise.all([
          this.loadCursosData(),
          this.loadTurnosData(),
          this.loadDisciplinasData(),
        ]);

        this.initializeForm(serie);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.$loaderService.close();
      }
    },

    async loadDisciplinasData() {
      this.disciplinasList = await this.$services.disciplinasService.syncAll();
    },

    async loadTurnosData() {
      this.loadingTurnos = true;
      try {
        this.turnosList = await this.$services.turnosService.syncAll();
      } finally {
        this.loadingTurnos = false;
      }
    },

    mapearPivots(disciplinas) {
      return (disciplinas || []).reduce(
        (mapa, disciplina) => ({ ...mapa, [disciplina.id]: disciplina.pivot || {} }),
        {}
      );
    },

    shouldDisableDisciplina(disciplinaId) {
      return this.isDisciplineSelected(disciplinaId) && this.hasRegistrosRelacionados(disciplinaId);
    },

    /** Cria a configuração de turnos recém-vinculados e descarta a dos removidos. */
    sincronizarConfiguracoes(turnoIdList) {
      const chavesValidas = [CHAVE_PADRAO, ...turnoIdList.map(String)];

      turnoIdList.forEach((turnoId) => {
        const chave = String(turnoId);
        if (this.configuracoes[chave]) return;

        const padrao = this.configuracoes[CHAVE_PADRAO];
        this.$set(
          this.configuracoes,
          chave,
          padrao ? this.clonarConfiguracao(padrao) : this.criarConfiguracao()
        );
      });

      Object.keys(this.configuracoes)
        .filter((chave) => !chavesValidas.includes(chave))
        .forEach((chave) => this.$delete(this.configuracoes, chave));

      if (this.activeTab >= chavesValidas.length) {
        this.activeTab = 0;
      }
    },

    sortByDescricao(list) {
      return [...list].sort((a, b) => (a.descricao || "").localeCompare(b.descricao || ""));
    },

    async submitForm() {
      this.submittingForm = true;

      const payload = {
        ...this.form,
        disciplinaIdList: this.buildDisciplinasPayload(CHAVE_PADRAO),
        variacoes: this.form.turnoIdList.map((turnoId) => ({
          turno_id: turnoId,
          disciplinas: this.buildDisciplinasPayload(String(turnoId)),
        })),
      };

      try {
        if (this.editing) {
          await this.$services.seriesService.atualizarSerie(payload);
          this.$toast.success("Etapa atualizada com sucesso");
        } else {
          await this.$services.seriesService.criarSerie(payload);
          this.$toast.success("Etapa criada com sucesso");
        }
        this.$router.push({ name: "series" });
      } catch (error) {
        this.$toast.danger(error?.error?.message || "Não foi possível salvar a etapa");
        setTimeout(() => {
          this.$toast.danger("Remova os registros antes de realizar a desvinculação.");
        }, 3000);
      } finally {
        this.submittingForm = false;
      }
    },

    toggleAllDisciplines() {
      const configuracao = this.configuracoes[this.abaAtual.chave];
      if (!configuracao) return;

      if (this.isSelectAll) {
        configuracao.disciplinaIdList = this.disciplinasList
          .filter((disciplina) => !this.hasGestao(disciplina.id))
          .map((disciplina) => disciplina.id);
      } else {
        configuracao.disciplinaIdList = configuracao.disciplinaIdList.filter((id) =>
          this.hasGestao(id)
        );
      }
    },

    turnoText(turno) {
      return turno.descricao;
    },

    turnoValue(turno) {
      return turno.id;
    },
  },
};
</script>

<style scoped>
.carga-horaria-field {
  max-width: 120px;
}

.required::after {
  color: red;
  content: " *";
}

.selected-disciplina {
  background-color: #e3f2fd;
  border: 2px solid #2196f3;
}

.v-card {
  border-radius: 8px;
  transition: all 0.3s ease;
}

.tooltip-registros {
  font-size: 14px;
  text-align: center;
}

.registros-table {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
  width: 100%;
}

.registros-row {
  align-items: center;
  display: grid;
  grid-template-columns: 1fr 1fr;
  text-align: center;
  width: 100%;
}
</style>
